import Decimal from "decimal.js";
import moment from "moment-timezone";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useListAgtechTicketsBySettlementOrAdvance,
  type AgtechTicketsContractInfo,
  type ExpandedTransformedEntry,
  type ExpandedTransformedSettlement,
  type ExpandedTransformedAdvance,
  type ExpandedGrade,
  type TransformedSplit,
} from "@/lib/api/agtech/ticketsExpanded";
import { Field, NumericField, GradesBlock, fmt, fmtNum } from "./TicketComponents";

// ── Types ──────────────────────────────────────────────────────────

interface ContractTicketsModalProps {
  contractNbr: number | null;
  contractId: string | null;
  settlementNbr: number | null;
  advanceNbr: number | null;
  onClose: () => void;
}

type FlatCard = {
  ticketId: string;
  entry: ExpandedTransformedEntry;
  splitPct: string | null;
  settlementAdvanceSerialNbr: string | null;
} & (
  | { kind: "settlement"; item: ExpandedTransformedSettlement }
  | { kind: "advance"; item: ExpandedTransformedAdvance }
);

// ── Helpers ────────────────────────────────────────────────────────

function findSplitPct(
  splits: TransformedSplit[],
  shipFromId: string | null,
): string | null {
  if (splits.length === 0) return null;
  if (splits.length === 1) return splits[0].split_percentage;
  const match = splits.find((sp) => sp.ticket_related_id === shipFromId);
  return match?.split_percentage ?? null;
}

function fmtDollar(val: string | null | undefined): string {
  if (val === null || val === undefined) return "—";
  return `$${new Decimal(val).toNumber().toLocaleString()}`;
}

function fmtSplitPct(pct: string | null): string {
  if (pct === null) return "—";
  return `${new Decimal(pct).times(100).toDP(2).toNumber()}%`;
}

// ── Cash formula line (local, mirrors TicketComponents) ────────────

function CashFormulaLine({
  label,
  cashApplied,
  cashCharges,
  cashAmount,
}: {
  label: string;
  cashApplied: string | null;
  cashCharges: string | null;
  cashAmount: string | null;
}) {
  if (cashAmount === null && cashApplied === null) return null;

  const appliedFmt =
    cashApplied !== null
      ? new Decimal(cashApplied).toNumber().toLocaleString()
      : null;
  const chargesFmt =
    cashCharges !== null
      ? new Decimal(cashCharges).toNumber().toLocaleString()
      : null;
  const amountFmt =
    cashAmount !== null
      ? new Decimal(cashAmount).toNumber().toLocaleString()
      : null;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">
        {label}
      </span>
      <div className="flex items-center gap-1 text-xs flex-wrap">
        {appliedFmt !== null && (
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">
            ${appliedFmt}
          </span>
        )}
        {chargesFmt !== null && (
          <>
            <span className="text-muted-foreground">−</span>
            <span className="text-red-500 dark:text-red-400 font-medium">
              ${chargesFmt}
            </span>
          </>
        )}
        {amountFmt !== null && (
          <>
            <span className="text-muted-foreground">=</span>
            <span className="font-semibold">${amountFmt}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── Grades inline ──────────────────────────────────────────────────

function CompactGrades({ grades }: { grades: ExpandedGrade[] }) {
  if (grades.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
      {grades.map((g, i) => (
        <div
          key={`${g.charge_grade_code}-${i}`}
          className="text-xs"
        >
          <span className="text-muted-foreground">{g.charge_grade_code}:</span>
          <span className="ml-0.5 font-medium">{fmt(g.charge_grade_value)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Contract summary card ───────────────────────────────────────────

function ContractSummaryCard({ rows }: { rows: AgtechTicketsContractInfo[] }) {
  if (rows.length === 0) return null;

  const totalPriced = rows.reduce((acc, r) => acc.plus(new Decimal(r.pricingRelease?.quantity ?? 0)), new Decimal(0));
  const totalSettled = rows.reduce((acc, r) => acc.plus(new Decimal(r.applied ?? 0)), new Decimal(0));
  const totalGross = rows.reduce((acc, r) => acc.plus(new Decimal(r.gross_dollars ?? 0)), new Decimal(0));
  const totalDiscounts = rows.reduce((acc, r) => acc.plus(new Decimal(r.discounts ?? 0)), new Decimal(0));
  const totalCharges = rows.reduce((acc, r) => acc.plus(new Decimal(r.charges ?? 0)), new Decimal(0));
  const totalSettlementAmount = totalGross.minus(totalDiscounts).minus(totalCharges);

  return (
    <div className="rounded-lg border bg-muted/50 p-3 space-y-2">
      <span className="text-xs font-semibold">Contract Summary</span>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-[10px] font-medium text-muted-foreground">
              <th className="text-left py-1 pr-3">Contract #</th>
              <th className="text-left py-1">Contract Type</th>
              <th className="text-left py-1">Contract Date</th>
              <th className="text-right py-1 pr-3">Price</th>
              <th className="text-right py-1 pr-3">Priced</th>
              <th className="text-right py-1 pr-3">Settled</th>
              <th className="text-right py-1 pr-3">Gross $</th>
              <th className="text-right py-1 pr-3">Discounts</th>
              <th className="text-right py-1 pr-3">Charges</th>
              <th className="text-right py-1">Settlement Amt</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="py-1 pr-3">{r.contract_id}</td>
                <td className="py-1">{r.pricing_type_code}</td>
                <td className="py-1">{r.contract_date ? moment.tz(r.contract_date, "UTC").format('MM/DD/YYYY') : ""}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.price)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.pricingRelease?.quantity)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.applied)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.gross_dollars)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.discounts)}</td>
                <td className="text-right py-1 pr-3">{fmtNum(r.charges)}</td>
                <td className="text-right py-1 font-semibold">
                  {fmtDollar(new Decimal(r.gross_dollars ?? 0).minus(new Decimal(r.discounts ?? 0)).minus(new Decimal(r.charges ?? 0)).toString())}
                </td>
              </tr>
            ))}
            <tr className="border-t border-border font-semibold">
              <td className="py-1.5 pr-3">Total</td>
              <td className="text-right py-1.5 pr-3"></td>
              <td className="text-right py-1.5"></td>
              <td className="text-right py-1.5"></td>
              <td className="text-right py-1.5 pr-3">{totalPriced.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalSettled.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalGross.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalDiscounts.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5 pr-3">{totalCharges.toNumber().toLocaleString()}</td>
              <td className="text-right py-1.5">${totalSettlementAmount.toNumber().toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Single card ────────────────────────────────────────────────────

function ContractTicketCard({ card }: { card: FlatCard }) {
  const { ticketId, entry, item, kind, splitPct } = card;

  const isSettlement = kind === "settlement";
  const cashLabel = isSettlement ? "Cash Settlement" : "Cash Advance";
  const cashAmount = isSettlement
    ? (item as ExpandedTransformedSettlement).cash_settlement_amount
    : (item as ExpandedTransformedAdvance).cash_advance_amount;

  const hasPricing =
    item.price !== null || item.net_price !== null;
  const hasCash =
    item.cash_applied !== null || cashAmount !== null;
  const hasChargesDetails = item.charges_details !== null;

  const grades = item.grades.filter((g) => !!g.charge_grade_value && g.charge_grade_value !== '0');

  const kindLabel = isSettlement ? "Settlement" : "Advance";
  const accentBorder = isSettlement
    ? "border-l-blue-500 dark:border-l-blue-400"
    : "border-l-violet-500 dark:border-l-violet-400";
  const kindColor = isSettlement
    ? "text-blue-600 dark:text-blue-400"
    : "text-violet-600 dark:text-violet-400";

  return (
    <div className={`rounded-lg border border-l-[3px] ${accentBorder} bg-card p-3 space-y-2`}>
      {/* Row 1: type label + ticket identifiers */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${kindColor}`}>
          {kindLabel}
        </span>
        <span className="text-muted-foreground text-[10px]">·</span>
        <span className="text-xs font-semibold">Ticket #{ticketId}</span>
        <span className="text-xs text-muted-foreground">
          Split: {fmtSplitPct(splitPct)}
        </span>
      </div>

      {/* Row 2: quantities */}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <Field label="Vehicle" value={fmt(entry.vehicle_id)} />
        <Field label="Ref" value={fmt(entry.reference_code)} />
        <NumericField label="Share" value={item.vp_quantity} uom={entry.dest_uom} />
        <NumericField label="Applied" value={item.vp_applied_quantity} uom={entry.dest_uom} />
      </div>

      {/* Row 3: grades */}
      {grades.length > 0 && (
        <div>
          <span className="text-[10px] font-medium text-muted-foreground">Grades</span>
          <CompactGrades grades={grades} />
        </div>
      )}

      {/* Row 4: pricing + charges + cash */}
      {(hasPricing || hasCash || hasChargesDetails) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-1.5">
          {hasChargesDetails && (
            <Field label="Charges Detail" value={item.charges_details!} />
          )}
          {item.price !== null && (
            <NumericField label="Price" value={item.price} uom="" />
          )}
          {item.net_price !== null && (
            <NumericField label="Net Price" value={item.net_price} uom="" />
          )}
          <CashFormulaLine
            label={cashLabel}
            cashApplied={item.cash_applied}
            cashCharges={item.cash_charges}
            cashAmount={cashAmount}
          />
        </div>
      )}
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────

export function ContractTicketsModal({
  contractNbr,
  contractId,
  settlementNbr,
  advanceNbr,
  onClose,
}: ContractTicketsModalProps) {
  const result = useListAgtechTicketsBySettlementOrAdvance({
    settlement_nbrs: settlementNbr !== null ? [settlementNbr] : undefined,
    advance_nbrs: advanceNbr !== null ? [advanceNbr] : undefined,
  });

  const isOpen = settlementNbr !== null || advanceNbr !== null;
  if (!isOpen) return null;

  const tickets = result.data?.data?.tickets?.data ?? [];
  const contractsInfo = result.data?.data?.contractsInfo ?? [];

  // Flatten tickets → entries → settlements/advances into cards
  const cards: FlatCard[] = [];
  for (const ticket of tickets) {
    for (const entry of ticket.entries) {
      for (const s of entry.settlements) {
        cards.push({
          kind: "settlement",
          ticketId: ticket.scale_ticket_id,
          entry,
          item: s,
          splitPct: findSplitPct(entry.splits, s.ship_from_id),
          settlementAdvanceSerialNbr: s.serial_nbr?.toString() ?? null,
        });
      }
      for (const a of entry.advances) {
        cards.push({
          kind: "advance",
          ticketId: ticket.scale_ticket_id,
          entry,
          item: a,
          splitPct: findSplitPct(entry.splits, a.ship_from_id),
          settlementAdvanceSerialNbr: a.serial_nbr?.toString() ?? null,
        });
      }
    }
  }

  // Sort by ticket id
  cards.sort((a, b) => a.ticketId.localeCompare(b.ticketId));

  const isLoading = result.isLoading;
  const isEmpty = !isLoading && cards.length === 0;

  const firstCard = cards?.[0];
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl lg:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{firstCard?.kind?.toLocaleUpperCase()} {firstCard?.settlementAdvanceSerialNbr ? `#${firstCard?.settlementAdvanceSerialNbr}` : ""}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-1">
          {isLoading && (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Loading tickets…
            </p>
          )}

          {isEmpty && (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No tickets found for this contract.
            </p>
          )}

          <ContractSummaryCard rows={contractsInfo} />

          {cards.map((card, i) => (
            <ContractTicketCard key={i} card={card} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
